import React, { useState } from "react";
import { motion } from "framer-motion";
import { Send, Mail, MapPin, Clock, Check } from "lucide-react";
import { toast } from "sonner";

const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [sent, setSent] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please share your name, email and a short note.");
      return;
    }
    setSent(true);
    toast.success("Thanks — we'll get back to you within a day.");
    setForm({ name: "", email: "", company: "", message: "" });
    setTimeout(() => setSent(false), 3500);
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-24 md:py-32 overflow-hidden"
    >
      <div aria-hidden className="absolute inset-0 soft-mesh opacity-80" />
      <div className="relative max-w-7xl mx-auto px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <span className="text-xs font-semibold tracking-[0.22em] uppercase text-[#0A82D8]">
            Get in touch
          </span>
          <h2 className="mt-3 font-display font-semibold text-4xl md:text-5xl lg:text-6xl tracking-tighter leading-[1.03]">
            Let's talk about <span className="text-gradient-cyan">your work.</span>
          </h2>
          <p className="mt-4 text-base md:text-lg text-muted-foreground max-w-xl mx-auto">
            Tell us what you're trying to solve. If it's in our lane, we'll tell you how we'd approach it. If not, we'll point you somewhere good.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.05 }}
            className="lg:col-span-2 flex flex-col gap-4"
          >
            <a
              href="mailto:Dmaskut@gmail.com"
              data-testid="contact-email"
              className="group glass-strong rounded-3xl p-6 flex items-start gap-4 hover:-translate-y-1 transition-transform"
            >
              <div
                className="h-11 w-11 rounded-xl flex items-center justify-center text-white"
                style={{ background: "linear-gradient(135deg,#00B4F0,#0A82D8)" }}
              >
                <Mail className="h-5 w-5" />
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">Email us</div>
                <div className="mt-1 font-display font-semibold text-lg group-hover:text-[#0A82D8] transition-colors">
                  Dmaskut@gmail.com
                </div>
              </div>
            </a>
            <div className="glass-strong rounded-3xl p-6 flex items-start gap-4">
              <div className="h-11 w-11 rounded-xl glass flex items-center justify-center">
                <Clock className="h-5 w-5" />
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">Reply time</div>
                <div className="mt-1 font-display font-semibold text-lg">Usually within 24 hours</div>
              </div>
            </div>
            <div className="glass-strong rounded-3xl p-6 flex items-start gap-4">
              <div className="h-11 w-11 rounded-xl glass flex items-center justify-center">
                <MapPin className="h-5 w-5" />
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">Based in</div>
                <div className="mt-1 font-display font-semibold text-lg">India · working remote-first</div>
              </div>
            </div>
          </motion.div>

          <motion.form
            onSubmit={onSubmit}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="lg:col-span-3 glass-strong rounded-3xl p-6 md:p-8"
            data-testid="contact-form"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field name="name" label="Your name" value={form.name} onChange={onChange} testid="input-name" />
              <Field name="email" type="email" label="Email" value={form.email} onChange={onChange} testid="input-email" />
            </div>
            <div className="mt-4">
              <Field name="company" label="Company (optional)" value={form.company} onChange={onChange} testid="input-company" />
            </div>
            <div className="mt-4">
              <label className="block text-[11px] uppercase tracking-[0.2em] text-muted-foreground mb-2">What do you need help with?</label>
              <textarea
                name="message"
                data-testid="input-message"
                rows={5}
                value={form.message}
                onChange={onChange}
                placeholder="A few lines about what you're working on..."
                className="w-full bg-transparent border border-border rounded-2xl px-4 py-3 text-sm outline-none focus:border-[#00B4F0] focus:ring-2 focus:ring-[#00B4F0]/20 transition resize-none"
              />
            </div>
            <div className="mt-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <p className="text-xs text-muted-foreground">
                We'll never share your details. One reply, no sales sequences.
              </p>
              <button
                type="submit"
                data-testid="contact-submit-btn"
                className="btn-primary"
              >
                {sent ? <><Check className="h-4 w-4" /> Sent</> : <>Send message <Send className="h-4 w-4" /></>}
              </button>
            </div>
          </motion.form>
        </div>
      </div>
    </section>
  );
};

const Field = ({ label, name, value, onChange, type = "text", testid }) => (
  <div>
    <label className="block text-[11px] uppercase tracking-[0.2em] text-muted-foreground mb-2">{label}</label>
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      data-testid={testid}
      className="w-full bg-transparent border border-border rounded-2xl px-4 py-3 text-sm outline-none focus:border-[#00B4F0] focus:ring-2 focus:ring-[#00B4F0]/20 transition"
    />
  </div>
);

export default Contact;
